import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  listarExpedientes,
  listarIngestaCola,
  obtenerIngestaMetricas,
} from "@/lib/api";
import type { ExpedienteAnalizado } from "@/types";
import { Header } from "@/components/Header";
import { useAuth } from "@/hooks/useAuth";

// Alertas críticas que hacen que el motor cape la confianza a 0.5 —
// espejo de _ALERTAS_CRITICAS_CAP_CONFIANZA en gema/expediente/pipeline.py.
// No son "errores del motor": marcan que el caso necesita revisión humana
// (típicamente una visita técnica) antes de actuar.
const ALERTAS_REVISION = new Set<string>([
  "DECISION_PROVISIONAL_REQUIERE_VISITA",
  "RIESGO_SILENCIO_ADMINISTRATIVO_POSITIVO",
  "NULIDAD_POR_FALTA_INVESTIGACION_PREVIA",
  "CONFIANZA_BAJA_REVISION_HUMANA",
  "CARTA_LLM_INVENTO_HECHOS_REVISAR",
  "URGENCIA_SEGURIDAD_INSPECCION_INMEDIATA",
  "FALLA_CONTINUIDAD_ART_137_REVISAR",
  "DATOS_INCONSISTENTES_REVISAR",
  "FECHA_FACTURA_SOSPECHOSA_REVISAR",
]);

// Etiqueta legible para el desglose de "motivos de revisión".
const ETIQUETA_ALERTA: Record<string, string> = {
  DECISION_PROVISIONAL_REQUIERE_VISITA: "Requiere visita técnica",
  RIESGO_SILENCIO_ADMINISTRATIVO_POSITIVO: "Riesgo de SAP (Art. 158)",
  NULIDAD_POR_FALTA_INVESTIGACION_PREVIA: "Nulidad Art. 149 (accede)",
  CONFIANZA_BAJA_REVISION_HUMANA: "Confianza baja — revisión",
  CARTA_LLM_INVENTO_HECHOS_REVISAR: "Carta con hechos a verificar",
  URGENCIA_SEGURIDAD_INSPECCION_INMEDIATA: "Urgencia de seguridad",
  FALLA_CONTINUIDAD_ART_137_REVISAR: "Falla de continuidad (Art. 137)",
  DATOS_INCONSISTENTES_REVISAR: "Datos inconsistentes",
  FECHA_FACTURA_SOSPECHOSA_REVISAR: "Fecha de factura sospechosa",
};

/**
 * KPIs operacionales (v0.10.28 Fase B.5).
 *
 * Calculados client-side a partir de los expedientes cargados y la cola
 * de auto-ingesta. Para un piloto con < 2 000 casos/día esto es suficiente.
 * En producción a escala, se moverá a un endpoint backend agregado.
 */
export function KpisPage() {
  // Visualización por perfil:
  //  · Analista    → operación: su bandeja, calidad de las decisiones.
  //  · Supervisor+ → + riesgo (SAP), reiteración y cola de atención.
  //  · Admin       → + salud de la plataforma (worker de auto-ingesta).
  const { sesion, puedeAprobar, puedeAdministrar } = useAuth();
  const esSupervisor = puedeAprobar();
  const esAdmin = puedeAdministrar();

  const expQ = useQuery({
    queryKey: ["expedientes-kpi"],
    queryFn: () => listarExpedientes({ limit: 500 }),
    refetchInterval: 60_000,
  });
  const colaQ = useQuery({
    queryKey: ["ingesta-cola-kpi"],
    queryFn: () => listarIngestaCola({ limit: 1000 }),
    refetchInterval: 30_000,
    enabled: esSupervisor, // la cola de atención es materia de supervisión
  });
  const metQ = useQuery({
    queryKey: ["ingesta-metricas"],
    queryFn: obtenerIngestaMetricas,
    refetchInterval: 30_000,
    enabled: esAdmin, // el worker es infraestructura → sólo administrador
  });

  const expedientes = expQ.data ?? [];
  const cola = colaQ.data?.items ?? [];
  const met = metQ.data;

  // Funnel
  const total = expedientes.length;
  const conDecision = expedientes.filter((e) => e.decision !== null).length;
  const pendInfo = expedientes.filter((e) => e.estado === "PENDIENTE_INFO").length;
  const aprobados = expedientes.filter((e) => e.estado === "APROBADO").length;
  const rechazados = expedientes.filter((e) => e.estado === "RECHAZADO").length;
  const enRevision = expedientes.filter((e) => e.estado === "PENDIENTE_REVISION").length;

  // Distribución por artículo (top 5)
  const articulosCount: Record<string, number> = {};
  for (const e of expedientes) {
    for (const a of e.decision?.decision.articulos ?? []) {
      articulosCount[a] = (articulosCount[a] || 0) + 1;
    }
  }
  const articulos = Object.entries(articulosCount)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  // SAP ratio
  const conSap = expedientes.filter((e) =>
    e.decision?.decision.articulos.includes("158"),
  ).length;
  const sapRatio = conDecision ? Math.round((conSap / conDecision) * 100) : 0;

  // Reiteración
  const conReiteracion = expedientes.filter((e) => e.analisis_reiteracion?.detectada).length;
  const conPatron = expedientes.filter((e) => e.analisis_reiteracion?.patron_reincidencia).length;
  const reitRatio = total ? Math.round((conReiteracion / total) * 100) : 0;

  // Confianza y motivos de revisión.
  //
  // El motor CAPA la confianza a 0.5 cuando una decisión carga una alerta
  // crítica (necesita visita técnica, nulidad Art. 149, datos inconsistentes,
  // riesgo SAP…). Por eso una "confianza baja" casi nunca significa "la
  // decisión es dudosa", sino "este caso está marcado para revisión humana".
  // Mostrar sólo el promedio crudo (≈60%) da la falsa impresión de que el
  // motor es poco fiable; en realidad decide con alta confianza en los casos
  // que NO exigen revisión, y marca el resto a propósito.
  const conDec = expedientes.filter((e) => e.decision != null);
  const estaMarcado = (e: ExpedienteAnalizado): boolean =>
    (e.decision?.metadata.alertas ?? []).some((a) =>
      ALERTAS_REVISION.has(a as string),
    );

  const marcados = conDec.filter(estaMarcado);
  const requierenRevision = marcados.length;

  // Confianza real: sólo entre los casos NO marcados para revisión.
  const confSinGate = conDec
    .filter((e) => !estaMarcado(e))
    .map((e) => e.decision!.metadata.confianza_motor);
  const confPromedio = confSinGate.length
    ? Math.round((confSinGate.reduce((a, b) => a + b, 0) / confSinGate.length) * 100)
    : 0;

  // Desglose de por qué se marcaron los casos (la señal accionable).
  const motivos: Record<string, number> = {};
  for (const e of marcados) {
    for (const a of e.decision?.metadata.alertas ?? []) {
      if (ALERTAS_REVISION.has(a as string)) {
        motivos[a as string] = (motivos[a as string] ?? 0) + 1;
      }
    }
  }
  const motivosOrdenados = Object.entries(motivos).sort(([, a], [, b]) => b - a);

  // Throughput (casos procesados hoy por el worker)
  const procesadosHoy = met
    ? met.ok_hoy + met.fallo_hoy + met.omitido_hoy
    : 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <main className="page page--wide">
        <div className="crumbs">
          <Link to="/">Pendientes CRM</Link>
          <span className="sep">/</span>
          <span>KPIs</span>
        </div>

        <div className="page-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              {esAdmin
                ? "Operación · Supervisión · Plataforma"
                : esSupervisor
                ? "Operación · Supervisión"
                : "Operación"}
            </div>
            <h1>KPIs {sesion ? `· ${sesion.rol}` : ""}</h1>
            <p className="sub">
              {esAdmin
                ? "Funnel, calidad, riesgo de SAP, reiteración y salud del worker de auto-ingesta."
                : esSupervisor
                ? "Funnel del expediente, calidad de las decisiones, riesgo de Silencio Administrativo y reiteración."
                : "Funnel del expediente, calidad de las decisiones y distribución por artículo."}
            </p>
          </div>
        </div>

        {/* Stat tiles — el conjunto cambia según el rol */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 16,
            marginBottom: 24,
          }}
        >
          <StatTile
            label="Confianza del motor"
            value={confSinGate.length ? `${confPromedio}%` : "—"}
            delta={`en ${confSinGate.length} casos sin alerta de revisión`}
            tone={confPromedio >= 80 ? "ok" : confPromedio >= 60 ? "ink" : "warn"}
          />
          {esSupervisor ? (
            <>
              <StatTile
                label="Requieren revisión"
                value={requierenRevision}
                delta={`${conDecision ? Math.round((requierenRevision / conDecision) * 100) : 0}% marcados por el motor`}
                tone="ink"
              />
              <StatTile
                label="Riesgo de SAP (Art 158)"
                value={conSap}
                delta={`${sapRatio}% de las decisiones`}
                tone={sapRatio > 15 ? "warn" : "ok"}
              />
            </>
          ) : (
            <>
              <StatTile
                label="En revisión"
                value={enRevision}
                delta="expedientes en tu bandeja"
                tone="ink"
              />
              <StatTile
                label="Pendiente info"
                value={pendInfo}
                delta="requieren datos del operador"
                tone={pendInfo > 0 ? "warn" : "ok"}
              />
            </>
          )}
          {esAdmin ? (
            <StatTile
              label="Procesados hoy"
              value={procesadosHoy}
              delta={met ? `${met.ok_hoy} OK · ${met.fallo_hoy} fallaron` : "—"}
            />
          ) : (
            <StatTile
              label="Con decisión"
              value={conDecision}
              delta={`de ${total} ingestados`}
              tone="ok"
            />
          )}
        </div>

        {/* Funnel + (admin) Auto-ingesta */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: esAdmin ? "2fr 1fr" : "1fr",
            gap: 16,
            marginBottom: 24,
          }}
        >
          <div className="card">
            <div className="card__head">
              <div className="icon">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M2 3h12l-3 5v4l-6 2v-6L2 3z" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Funnel del expediente</h3>
              <div className="meta">Distribución por estado</div>
            </div>
            <div className="card__body">
              <FunnelStep label="Total ingestados" valor={total} max={total} tone="emerald" />
              <FunnelStep label="Con decisión" valor={conDecision} max={total} tone="emerald" />
              <FunnelStep label="En revisión" valor={enRevision} max={total} tone="amber" />
              <FunnelStep label="Pendiente info" valor={pendInfo} max={total} tone="orange" />
              <FunnelStep label="Aprobados" valor={aprobados} max={total} tone="emerald" />
              <FunnelStep label="Rechazados" valor={rechazados} max={total} tone="red" />
            </div>
          </div>

          {esAdmin && (
          <div className="card">
            <div className="card__head">
              <div className="icon">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="8" cy="8" r="6" />
                  <path d="M8 4v4l2 2" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Auto-ingesta del CRM</h3>
            </div>
            <div className="card__body">
              {met ? (
                <dl className="kv">
                  <dt>Worker</dt>
                  <dd>
                    {met.worker_activo ? (
                      <span className="badge badge--green">
                        <span className="dot" />
                        Activo
                      </span>
                    ) : (
                      <span className="badge badge--red">
                        <span className="dot" />
                        Inactivo
                      </span>
                    )}
                  </dd>
                  <dt>Intervalo</dt>     <dd className="mono">{met.configuracion.intervalo_seg ?? "—"} s</dd>
                  <dt>Batch</dt>         <dd className="mono">{met.configuracion.batch_size ?? "—"}</dd>
                  <dt>Concurrencia</dt>  <dd className="mono">{met.configuracion.concurrencia ?? "—"}</dd>
                  <dt>En cola</dt>       <dd className="mono">{met.pendientes}</dd>
                  <dt>Procesando</dt>    <dd className="mono">{met.procesando}</dd>
                  <dt>OK hoy</dt>        <dd className="mono">{met.ok_hoy}</dd>
                  <dt>Fallaron hoy</dt>  <dd className="mono">{met.fallo_hoy}</dd>
                  <dt>Pend. info hoy</dt><dd className="mono">{met.pendiente_info_hoy}</dd>
                </dl>
              ) : (
                <span className="muted">Cargando…</span>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Motivos de revisión — por qué el motor marcó los casos.
            Reencuadra "confianza baja" como una lista de acciones concretas. */}
        {esSupervisor && requierenRevision > 0 && (
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card__head">
              <h3>Motivos de revisión</h3>
              <div className="meta">
                {requierenRevision} casos marcados por el motor · no son
                errores, son decisiones que necesitan una acción humana
              </div>
            </div>
            <div className="card__body">
              <div className="stack stack-3">
                {motivosOrdenados.map(([alerta, n]) => {
                  const pct = Math.round((n / requierenRevision) * 100);
                  return (
                    <div key={alerta}>
                      <div className="row row-2" style={{ fontSize: 13, marginBottom: 4 }}>
                        <span>{ETIQUETA_ALERTA[alerta] ?? alerta}</span>
                        <span className="row-end mono muted">{n} · {pct}%</span>
                      </div>
                      <div className="meter__bar">
                        <div className="meter__fill" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                Reiteración detectada en {conReiteracion} casos ({reitRatio}%),
                de los cuales {conPatron} muestran patrón de reincidencia (≥3).
              </p>
            </div>
          </div>
        )}

        {/* Distribución por artículo + (supervisor+) Cola de atención */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: esSupervisor ? "1fr 1fr" : "1fr",
            gap: 16,
          }}
        >
          <div className="card">
            <div className="card__head">
              <h3>Distribución por artículo</h3>
              <div className="meta">Top 6 más citados</div>
            </div>
            <div className="card__body">
              {articulos.length === 0 ? (
                <span className="muted">Aún no hay decisiones registradas.</span>
              ) : (
                <div className="stack stack-3">
                  {articulos.map(([art, count]) => {
                    const pct = Math.round((count / conDecision) * 100);
                    return (
                      <div key={art}>
                        <div
                          className="row row-2"
                          style={{ fontSize: 13, marginBottom: 4 }}
                        >
                          <span className="mono">Art. {art}</span>
                          <span className="row-end mono muted">
                            {count} · {pct}%
                          </span>
                        </div>
                        <div className="meter__bar">
                          <div
                            className="meter__fill"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {esSupervisor && (
          <div className="card">
            <div className="card__head">
              <h3>Cola del worker — atención</h3>
              <div className="meta">Fallos y casos pendientes de info</div>
            </div>
            <div className="card__body">
              <ListaProblemas cola={cola} expedientes={expedientes} />
            </div>
          </div>
          )}
        </div>
      </main>
    </div>
  );
}

// ─── Componentes internos ───────────────────────────────────── //

function StatTile({
  label,
  value,
  delta,
  tone = "ink",
}: {
  label: string;
  value: string | number;
  delta?: string;
  tone?: "ok" | "warn" | "ink";
}) {
  const deltaColor = {
    ok: "var(--emerald-deep)",
    warn: "var(--amber-deep)",
    ink: "var(--ink-3)",
  }[tone];
  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="stat">
        <span className="stat__label">{label}</span>
        <span className="stat__value">{value}</span>
        {delta && (
          <span className="stat__delta" style={{ color: deltaColor }}>
            {delta}
          </span>
        )}
      </div>
    </div>
  );
}

function FunnelStep({
  label,
  valor,
  max,
  tone,
}: {
  label: string;
  valor: number;
  max: number;
  tone: "emerald" | "amber" | "orange" | "red";
}) {
  const pct = max ? Math.round((valor / max) * 100) : 0;
  const fillColor = {
    emerald: "var(--emerald)",
    amber: "var(--amber)",
    orange: "var(--orange)",
    red: "var(--red)",
  }[tone];
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="row row-2" style={{ fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span>
        <span className="row-end mono">
          <strong style={{ color: "var(--ink)" }}>{valor}</strong>{" "}
          <span className="muted">· {pct}%</span>
        </span>
      </div>
      <div
        style={{
          height: 8,
          background: "var(--surface-sunken)",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: fillColor,
            borderRadius: 4,
            transition: "width .3s",
          }}
        />
      </div>
    </div>
  );
}

function ListaProblemas({
  cola,
  expedientes,
}: {
  cola: import("@/lib/api").IngestaColaItem[];
  expedientes: ExpedienteAnalizado[];
}) {
  const fallos = cola.filter((c) => c.estado === "FALLO");
  const pendInfo = expedientes.filter((e) => e.estado === "PENDIENTE_INFO");

  if (fallos.length === 0 && pendInfo.length === 0) {
    return (
      <span className="muted">
        ✓ Sin problemas: todos los casos del worker están al día.
      </span>
    );
  }

  return (
    <div className="stack stack-3" style={{ fontSize: 13 }}>
      {fallos.length > 0 && (
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            Fallos del worker · {fallos.length}
          </div>
          <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
            {fallos.slice(0, 5).map((c) => (
              <li
                key={c.radicado}
                style={{
                  padding: "6px 0",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <span className="mono" style={{ fontSize: 12 }}>
                  {c.radicado}
                </span>{" "}
                <span className="muted" style={{ fontSize: 12 }}>
                  · {c.intentos} intentos
                </span>
                {c.ultimo_error && (
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                    {c.ultimo_error.slice(0, 120)}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {fallos.length > 5 && (
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              … y {fallos.length - 5} más. Ver en{" "}
              <Link to="/" style={{ color: "var(--emerald-deep)" }}>
                Pendientes CRM
              </Link>
              .
            </div>
          )}
        </div>
      )}

      {pendInfo.length > 0 && (
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            Pendiente info · {pendInfo.length}
          </div>
          <p className="muted" style={{ fontSize: 12, margin: 0 }}>
            Expedientes procesados pero requieren que el operador
            complete datos por chat.
          </p>
          <Link
            to="/expedientes?estado=PENDIENTE_INFO"
            className="btn btn--ghost btn--sm"
            style={{ marginTop: 6 }}
          >
            Ver en bandeja →
          </Link>
        </div>
      )}
    </div>
  );
}
